import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

type SnackbarProps = {
  message: string
  trigger: number
  delayMs?: number
  durationMs?: number
}

const DEFAULT_DELAY_MS = 140
const DEFAULT_DURATION_MS = 3200

export function Snackbar({
  message,
  trigger,
  delayMs = DEFAULT_DELAY_MS,
  durationMs = DEFAULT_DURATION_MS,
}: SnackbarProps) {
  const [isVisible, setIsVisible] = useState(false)
  const hasMountedRef = useRef(false)
  const showDelayTimerRef = useRef<number | null>(null)
  const hideTimerRef = useRef<number | null>(null)

  const clearTimers = () => {
    if (showDelayTimerRef.current !== null) {
      window.clearTimeout(showDelayTimerRef.current)
    }
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current)
    }
  }

  useEffect(() => {
    return () => {
      clearTimers()
    }
  }, [])

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      return
    }

    if (trigger <= 0) {
      return
    }

    clearTimers()
    setIsVisible(false)

    showDelayTimerRef.current = window.setTimeout(() => {
      setIsVisible(true)
      hideTimerRef.current = window.setTimeout(() => {
        setIsVisible(false)
      }, durationMs)
    }, delayMs)
  }, [delayMs, durationMs, trigger])

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-5 z-50 flex justify-center px-4">
      <AnimatePresence>
        {isVisible ? (
          <motion.p
            role="status"
            aria-live="polite"
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-full rounded-lg bg-[var(--color-on-surface)] px-4 py-2 text-sm font-medium text-[var(--color-surface)] shadow-xl"
          >
            {message}
          </motion.p>
        ) : null}
      </AnimatePresence>
    </div>
  )
}
